import { FC, type JSX, MouseEvent, useState, useContext } from "react";
import { Link } from "react-router-dom";

import { TrainingLink } from "@/features/training/model/Training/TrainingLink";
import { EditTrainingLink } from "@/features/training/pages/training-tab/panel-left/accordion/EditTrainingLink";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip } from "@mui/material";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";

import { GetLanguageValueAsString } from "@/app/application/Translate";


interface TrainingLinkItemProps {
	link: TrainingLink;
}

export const TrainingLinkItem: FC<TrainingLinkItemProps> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const [editLinkOpen, toggleEditLinkModal] = useState(false);

	const customLink = buildCustomLink(props.link);

	return (
		<>
			<TreeItem
				itemId={`link-${props.link.Id}`}
				label={
					<Box
						id={`tree-node-link-${props.link.Id}`}
						component="span"
						sx={{ display: "flex", alignItems: "center", width: "100%" }}
					>
						{customLink}
						<div className="accordion-item-action">
							<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingLinkItem.L32C23.EditLink", "Edit link")} disableInteractive>
								<span>
									<IconButton
										size="small"
										onClick={(e: MouseEvent<HTMLButtonElement>) => {
											e.stopPropagation();
											toggleEditLinkModal(true);
										}}
										sx={{
											opacity: 0.6,
											"&:hover": { opacity: 1 },
										}}
										aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.TrainingTab.PanelLeft.Accordion.TrainingLinkItem.L44C22.EditLink", "Edit link")}
									>
										<EditOutlinedIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</div>
					</Box>
				}
			/>
			<EditTrainingLink
				open={editLinkOpen}
				handleClose={() => toggleEditLinkModal(false)}
				linkId={props.link.Id}
			/>
		</>
	);
};

function buildCustomLink(link: TrainingLink): JSX.Element {
	return link.UrlPath.startsWith("http") ? (
		<a
			href={link.UrlPath}
			target="_blank"
			className="accordion-item-link accordion-item-link-action"
			rel="noreferrer"
		>
			<OpenInNewIcon
				sx={{
					marginRight: "1rem",
				}}
			/>
			{link.Name}
		</a>
	) : link.UrlPath.startsWith("www.") ? (
		<a
			href={`http://${link.UrlPath}`}
			target="_blank"
			className="accordion-item-link accordion-item-link-action"
			rel="noreferrer"
		>
			<OpenInNewIcon
				sx={{
					marginRight: "1rem",
				}}
			/>
			{link.Name}
		</a>
	) : (
		<Link
			to={link.UrlPath[0] === "/" ? link.UrlPath : `/${link.UrlPath}`}
			relative="path"
			className="accordion-item-link accordion-item-link-action"
		>
			<OpenInNewIcon
				sx={{
					marginRight: "1rem",
				}}
			/>
			{link.Name}
		</Link>
	);
}
